// The college pages' Watch section (#4608, founder pick B, 2026-10-17): our
// published Shorts and tutorials about the school, from the public read
// get-college-videos (profile_manager_v2 college_videos_public.py, built by
// marketing_agent college_videos.py from the published-post records).
// Pure helpers: the order and names of the places, the tagged outbound link,
// the time and date lines, and the page's VideoObject structured data.

// The order a card lists its places in. Twin of college_videos.py CHANNELS
// (pinned by tests/cloud_functions/marketing_agent/test_college_videos.py).
export const CHANNELS = [
    { key: 'youtube', name: 'YouTube' },
    { key: 'instagram', name: 'Instagram' },
    { key: 'tiktok', name: 'TikTok' },
    { key: 'facebook', name: 'Facebook' },
    { key: 'linkedin', name: 'LinkedIn' },
];

// The newest five; Michigan and MIT have six (2026-10-17).
export const SHOWN = 5;

// The /videos page's themes, in chip order (#4728). Twin of
// college_videos.py THEMES (pinned by tests/cloud_functions/marketing_agent/
// test_college_videos.py): a Short's YouTube playlist, or How to Get Into for
// a college tutorial. A video with no theme shows under All only.
export const THEMES = [
    'What Colleges Weigh',
    'Early, Rounds & Deadlines',
    'What It Really Costs',
    'Majors & Doors',
    'Waitlists & Decisions',
    'Applying from Abroad',
    'How to Get Into',
];

const LINK_HOSTS = {
    youtube: 'www.youtube.com',
    instagram: 'www.instagram.com',
    tiktok: 'www.tiktok.com',
    facebook: 'www.facebook.com',
    linkedin: 'www.linkedin.com',
};

const isVideo = (v) => v && typeof v === 'object' && typeof v.id === 'string' && typeof v.fact === 'string'
    && typeof v.date === 'string' && v.links && typeof v.links === 'object'
    && CHANNELS.some((c) => typeof v.links[c.key] === 'string');

/** The videos the page shows: well-formed ones, newest first, at most SHOWN. */
export function shownVideos(list) {
    if (!Array.isArray(list)) return [];
    return list.filter(isVideo)
        .slice()
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
        .slice(0, SHOWN);
}

/** The places this video is live, in CHANNELS order: [{key, name, url}]. */
export function placesOf(video) {
    return CHANNELS.filter((c) => {
        const url = video.links[c.key];
        if (typeof url !== 'string') return false;
        try {
            const u = new URL(url);
            return u.protocol === 'https:' && u.host === LINK_HOSTS[c.key];
        } catch {
            return false;
        }
    }).map((c) => ({ ...c, url: video.links[c.key] }));
}

/**
 * The outbound link with our own tags. The platforms do not read utm tags
 * (they see a referral from our site); the click event is the measure.
 */
export function taggedLink(url, { video, channel, medium = 'college_page', content = channel }) {
    try {
        const u = new URL(url);
        u.searchParams.set('utm_source', 'stratiaadmissions');
        u.searchParams.set('utm_medium', medium);
        if (video) u.searchParams.set('utm_campaign', video);
        u.searchParams.set('utm_content', content);
        return u.toString();
    } catch {
        return url;
    }
}

/** "PT57S" -> "0:57", "PT3M49S" -> "3:49"; null when it is not a duration. */
export function clockOf(duration) {
    const m = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(String(duration || ''));
    if (!m || !(m[1] || m[2] || m[3])) return null;
    const h = Number(m[1] || 0);
    const min = Number(m[2] || 0);
    const s = String(Number(m[3] || 0)).padStart(2, '0');
    return h ? `${h}:${String(min).padStart(2, '0')}:${s}` : `${min}:${s}`;
}

/** "Oct 17": the day it went live, in Pacific time (where we post from). */
export function dayOf(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' });
}

const playsOnYouTube = (v) => typeof v.youtube_id === 'string' && /^[A-Za-z0-9_-]{11}$/.test(v.youtube_id);

const videoObject = (v) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: v.topic || v.fact,
    description: v.fact,
    thumbnailUrl: [v.poster || `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`],
    uploadDate: v.date,
    ...(v.duration ? { duration: v.duration } : {}),
    embedUrl: `https://www.youtube.com/embed/${v.youtube_id}`,
    publisher: { '@type': 'Organization', name: 'Stratia Admissions', url: 'https://stratiaadmissions.com' },
});

/** VideoObject for each shown video that plays on YouTube. */
export function videoJsonLd(videos) {
    return shownVideos(videos).filter(playsOnYouTube).map(videoObject);
}

// ── the /videos page (#4728) ────────────────────────────────────────────────

/**
 * Every well-formed video in the get-college-videos answer ({slug: [video]}),
 * each with its `college` slug, newest first. `known(slug)` keeps only
 * schools that have a college page (each card links back to it).
 */
export function allVideos(colleges, known = () => true) {
    if (!colleges || typeof colleges !== 'object') return [];
    const out = [];
    for (const [slug, list] of Object.entries(colleges)) {
        if (!known(slug) || !Array.isArray(list)) continue;
        for (const v of list) if (isVideo(v) && placesOf(v).length) out.push({ ...v, college: slug });
    }
    return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.id < b.id ? 1 : -1));
}

/** The page's sections: one per school, the school with the newest video first. */
export function groupByCollege(list) {
    const groups = new Map();
    for (const v of list) {
        if (!groups.has(v.college)) groups.set(v.college, []);
        groups.get(v.college).push(v);
    }
    return [...groups].map(([slug, videos]) => ({ slug, videos }));
}

/**
 * VideoObject once per video (founder 2026-10-18 2:42 PM PT: the college
 * pages show these too, so the two must not compete in search). A college
 * page is the home of the videos it shows (videoJsonLd of its own list, the
 * newest SHOWN); /videos states only the ones no college page shows.
 */
export function videosPageJsonLd(list) {
    const homed = new Set();
    for (const { videos } of groupByCollege(list)) {
        for (const v of shownVideos(videos)) homed.add(v.id);
    }
    return list.filter((v) => !homed.has(v.id) && playsOnYouTube(v)).map(videoObject);
}
